/**
 * App code files need a person's approval (owner 2026-10-27, docs/plans/2026-10-27_CODE_FILE_NEEDS_APPROVAL.md).
 *
 * THE rule module — every decision about "who set this code file's content" and "may it run as the app" is made here,
 * so it is managed in one place (owner: "make a helper function, it should be centralized"). The storage stamp
 * (`TreeStorage.createVersion`), the Bin snapshot / restore, copy, Approve, the drive listing and every runner
 * (`codeFileRunVerdict` in @papan/database: scripts.run, hooks, db.run ops) call these functions — never their own copy.
 *
 * An app runs its `.py` / `.sql` files AS THE APP. A file whose current content the app itself wrote (for example a
 * visitor's upload it saved under the visitor's file name) must not run with the app's grants until a person who may
 * edit the app approves it — the `.chain` "Needs approval" rule (D17/D24), for code.
 */
import { isAccountUserId } from './versions.js'

/** A code file's recorded author kind (`files.code_author_kind`, 0344). NULL = none recorded ⇒ Needs approval. */
export type CodeAuthorKind = 'user' | 'legacy' | 'app' | 'share' | 'system' | 'restore' | 'copy'

/** Is `name` an app code file — run as the app by `scripts.run` / hooks (`.py`) or `db.run` (`.sql`)? Case-insensitive
 *  (#166: every suffix test ignores case). */
export function isCodeFileName(name: string): boolean {
  const n = name.toLowerCase()
  return n.endsWith('.py') || n.endsWith('.sql')
}

/** May a file with this recorded author run as the app? Only a PERSON's content (`user`) and files that predate the
 *  rule (`legacy`). Anything else — the app, an anonymous link visitor, the system, a restore or copy that vouched for
 *  nothing, or no record at all — is Needs approval (fail closed: a missed writer greys a file, it never runs it). */
export function codeAuthorRunnable(kind: string | null | undefined): boolean {
  return kind === 'user' || kind === 'legacy'
}

/** May this code file run as the app NOW? Its recorded author runs (`codeAuthorRunnable`) AND that author vouched for
 *  exactly the content it holds (`code_author_hash` = `content_hash`, 0345): content written by any path that recorded
 *  no author — an older instance mid-deploy, any writer that bypassed the stamp — no longer matches, so it needs approval
 *  (fail closed), with no sweep. The ONE answer for the runners, the op bundle, the listing and Approve. */
export function codeFileRunnable(row: { codeAuthorKind?: string | null; codeAuthorHash?: string | null; contentHash?: string | null }): boolean {
  return codeAuthorRunnable(row.codeAuthorKind) && !!row.codeAuthorHash && row.codeAuthorHash === row.contentHash
}

/** The row's author AS IT STANDS for the content it holds now: a runnable author whose vouched hash no longer matches
 *  vouches for nothing (none recorded); any other author stays as recorded. What a copy reads off its source. */
export function codeAuthorOfContent(row: { codeAuthorKind?: string | null; codeAuthorId?: string | null; codeAuthorHash?: string | null; contentHash?: string | null }): { kind: string | null; id: string | null } {
  if (codeAuthorRunnable(row.codeAuthorKind) && !codeFileRunnable(row)) return { kind: null, id: null }
  return { kind: row.codeAuthorKind ?? null, id: row.codeAuthorId ?? null }
}

/**
 * Version sources (`file_versions.source`) whose act does NOT vouch for the content, whoever performed it — as for a
 * `.chain` (D17): a copy, a restore, an extract, Save to Drive, a move-Replace, the engine's own snapshots. A person's own
 * upload, pressed Save, Save As, rename, the AI agent acting for them and their token / MCP writes DO vouch (owner
 * 2026-10-27: "ofcuz ai agent count").
 */
const NON_VOUCHING_SOURCES = new Set([
  'restore', 'quarantine-restore', 'bin', 'copy', 'copied', 'paste', 'cross-tenant-copy', 'save-to-drive', 'share-save', 'move',
  'extract', 'handoff-save', 'compaction', 'reconcile', 'snapshot',
])
const RESTORE_SOURCES = new Set(['restore', 'quarantine-restore', 'bin'])
/** Source prefixes written by an APP (its SDK calls, an app-oauth client, the db engine). */
const APP_SOURCE_PREFIXES = ['sdk-', 'app-', 'appdb', 'm2m-', 'pointer-']

/** The writer of a version, as `TreeStorage.createVersion` sees it (`VersionActor`, structurally — this module has no
 *  dependency on the versions types). */
export interface CodeAuthorWrite {
  createdBy?: { type: string; id: string; appFolderId?: string; visitorUserId?: string } | null
  source: string
  appFolderId?: string | null
}

/** Who set a code file's content by this write — the ONE mapping from a write to `code_author_kind` / `_id`. */
export function codeAuthorOfWrite(w: CodeAuthorWrite): { kind: CodeAuthorKind; id: string | null } {
  const by = w.createdBy ?? null
  const app = w.appFolderId ?? by?.appFolderId ?? null
  if (w.appFolderId || APP_SOURCE_PREFIXES.some((p) => w.source.startsWith(p))) return { kind: 'app', id: app }
  if (!by) return { kind: 'system', id: null }
  if (by.type === 'app-actor') return { kind: 'app', id: app }
  if (by.type === 'system') return by.id.startsWith('app:') ? { kind: 'app', id: by.id.slice(4) } : { kind: 'system', id: null }
  // a PERSON: a user, or a share-link visitor with a recorded account (#849). An anonymous visitor is not one.
  let personId: string | null = null
  if (by.type === 'share') {
    if (!by.visitorUserId) return { kind: 'share', id: by.id }
    personId = by.visitorUserId
  } else if (by.type === 'user') {
    // an API key is a machine (as for a `.chain`, D17), not a person: its synthetic user `apikey:<id>` — like every
    // synthetic principal id it carries a colon (`isAccountUserId`). A person's own access token (MCP) is their real
    // account id, and counts.
    if (!isAccountUserId(by.id)) return { kind: 'system', id: null }
    personId = by.id
  } else {
    return { kind: 'system', id: null }
  }
  // …and even a person's act vouches for nothing when it only carries bytes along (a restore, a copy, an extract, a save
  // of content from elsewhere) — whoever performs it
  if (RESTORE_SOURCES.has(w.source)) return { kind: 'restore', id: null }
  if (NON_VOUCHING_SOURCES.has(w.source)) return { kind: 'copy', id: null }
  return { kind: 'user', id: personId }
}

/**
 * A copy's author (TreeStorage.copy, after it replays the source's history): an APPROVED source copied by a PERSON stays
 * approved, as theirs (as a `.chain` copy, D17); an approved source copied by anyone else, and an unapproved source
 * copied by anyone, keep needing approval.
 */
export function codeAuthorOfCopy(
  source: { kind: string | null | undefined; id: string | null | undefined },
  copier: { type: string; id: string } | null | undefined,
): { kind: CodeAuthorKind; id: string | null } {
  const person = copier?.type === 'user' && isAccountUserId(copier.id) ? copier.id
    : copier?.type === 'share' && (copier as { visitorUserId?: string }).visitorUserId ? (copier as { visitorUserId?: string }).visitorUserId! : null
  if (codeAuthorRunnable(source.kind) && person) return { kind: 'user', id: person }
  if (codeAuthorRunnable(source.kind)) return { kind: 'copy', id: null }
  return { kind: (source.kind as CodeAuthorKind | null) ?? 'copy', id: source.id ?? null }
}

/**
 * Run a VERIFIED script (owner 2026-10-27): the runner checked the script's saved author and hashed its bytes; it then
 * runs THOSE bytes, fed on stdin to this bootstrap — never the path again, which another run of the same app could
 * rewrite between the check and the exec. The bootstrap makes the run look like `python3 <path> <args…>`: `__file__` is
 * the path, `sys.argv` is `[path, …args]`, the script's own folder is first on `sys.path` (sibling imports work), and the
 * script runs in a real `__main__` module (`sys.modules['__main__']` — pickling its classes, `typing.get_type_hints`,
 * forked multiprocessing work as under `python3 <path>`; a spawn / forkserver child never re-reads the path — it skips the
 * main script, as for `python -c`, so functions it needs from the script itself must live in an imported module). The bytes are compiled as bytes, so a PEP 263 encoding line
 * and non-UTF-8 source behave as they would from the file. An
 * app script got no stdin before either (it read EOF), so stdin carrying the source changes nothing it sees.
 */
export const VERIFIED_PYTHON_BOOTSTRAP = [
  'import sys, os, types, io, tokenize, linecache, importlib.machinery',
  'p = sys.argv[1]',
  'src = sys.stdin.buffer.read()',
  'sys.stdin = open(os.devnull)',
  'sys.argv = sys.argv[1:]',
  // `-P` (below) left the working directory — the app root — OFF the path for the imports above, so no file the app
  // wrote there (`tokenize.py`) loads before the checked bytes; the script's own folder goes first, as `python3 <path>`
  'sys.path.insert(0, os.path.dirname(os.path.abspath(p)))',
  "m = types.ModuleType('__main__')",
  'm.__file__ = p',
  'm.__builtins__ = __builtins__',
  // spec named __main__: a spawn / forkserver child then skips re-running the main script (as for `python -c`) rather
  // than re-reading the path from disk — the unchecked file would run as the app
  "m.__spec__ = importlib.machinery.ModuleSpec('__main__', None)",
  // tracebacks show the lines that RAN, not the file on disk (mtime None: linecache never re-reads it)
  "linecache.cache[p] = (len(src), None, src.decode(tokenize.detect_encoding(io.BytesIO(src).readline)[0], 'replace').splitlines(True), p)",
  "sys.modules['__main__'] = m",
  "exec(compile(src, p, 'exec'), m.__dict__)",
].join('\n')

/** The argv that runs `scriptPath` from its verified bytes (stdin = those bytes) — see {@link VERIFIED_PYTHON_BOOTSTRAP}. */
export function verifiedPythonArgv(scriptPath: string, ...args: string[]): string[] {
  return ['python3', '-P', '-c', VERIFIED_PYTHON_BOOTSTRAP, scriptPath, ...args]
}
